import FormDatePicker from 'components/form/FormDatePicker';
import { AppConst } from 'constants/appConst';
import { DivConst } from 'constants/divConst';
import { format } from 'date-fns';
import { AppHelper } from 'helpers/appHelper';
import { useApiState } from 'hooks/useApiState';
import { useAppState } from 'hooks/useAppState';
import { useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { AcctState } from 'stores/atoms/acctState';

/**
 * レポートダウンロードモーダル
 */
export default function WsModalReportDownload(props: {
  setOpen: (open: boolean) => void;
  open: boolean;
  cd: string;
  contentsType: number;
  reportDateFrom: string;
  reportDateTo: string;
}): JSX.Element {
  /* ----------------------------------------------------------------------- */
  /* 型定義 */
  /* ----------------------------------------------------------------------- */
  type FormStateType = {
    reportDateFrom: Date;
    reportDateTo: Date;
  };

  /* ----------------------------------------------------------------------- */
  /* 変数定義 */
  /* ----------------------------------------------------------------------- */
  const { showBoundary } = useErrorBoundary();

  const {
    postDiagnosisReportDownload,
    postDiagnosisRespondentDownload,
    postQuizReportDownload,
    postQuizRespondentDownload,
  } = useApiState();

  const [processing, setProcessing] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const { initializeAcctState } = useAppState();

  const [acctState] = useRecoilState(AcctState);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormStateType>();

  const navigate = useNavigate();

  /* ----------------------------------------------------------------------- */
  /* ライフサイクル関数定義 */
  /* ----------------------------------------------------------------------- */

  /* ----------------------------------------------------------------------- */
  /* イベントハンドラ関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * モーダルを閉じた時の処理
   */
  const onClose = () => {
    props.setOpen(false);
  };

  /**
   * モーダルが開いた後の処理
   */
  const onAfterOpen = () => {
    setValue('reportDateFrom', props.reportDateFrom ? new Date(props.reportDateFrom) : null);
    setValue('reportDateTo', props.reportDateTo ? new Date(props.reportDateTo) : null);

    setInitialized(true);
  };

  /**
   * レポートCSVボタン押下時の処理
   */
  const onDownloadReport = () =>
    AppHelper.submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          const reportDateFrom = data.reportDateFrom
            ? format(data.reportDateFrom, 'yyyy/MM/dd')
            : null;

          const reportDateTo = data.reportDateTo ? format(data.reportDateTo, 'yyyy/MM/dd') : null;

          let response = null;
          // レポートダウンロードAPIの呼び出し
          switch (props.contentsType) {
            case DivConst.items.contentsType.quiz.value:
              response = await postQuizReportDownload({
                quizCd: props.cd,
                reportDateFrom: reportDateFrom,
                reportDateTo: reportDateTo,
              });
              break;
            case DivConst.items.contentsType.diagnosis.value:
              response = await postDiagnosisReportDownload({
                diagnosisCd: props.cd,
                reportDateFrom: reportDateFrom,
                reportDateTo: reportDateTo,
              });
              break;
            default:
          }
          // ファイルのダウンロード
          _download(response.fileName, response.fileData);
        } catch (e) {
          if (e.response.statusCode === AppConst.httpStatusCode.entryUnprocessable) {
            // トライアルプラン（利用期間切れ）の場合はTOPに遷移
            initializeAcctState(acctState.company.cd);
            navigate('/');
          }
          showBoundary(e);
        }
      })
    );

  /**
   * 獲得リストCSVボタン押下時の処理
   */
  const onDownloadRespondent = () =>
    AppHelper.submit(
      processing,
      setProcessing,
      handleSubmit(async (data: FormStateType) => {
        try {
          const reportDateFrom = data.reportDateFrom
            ? format(data.reportDateFrom, 'yyyy/MM/dd')
            : null;

          const reportDateTo = data.reportDateTo ? format(data.reportDateTo, 'yyyy/MM/dd') : null;

          let response = null;

          switch (props.contentsType) {
            case DivConst.items.contentsType.quiz.value:
              response = await postQuizRespondentDownload({
                quizCd: props.cd,
                reportDateFrom: reportDateFrom,
                reportDateTo: reportDateTo,
              });
              break;
            case DivConst.items.contentsType.diagnosis.value:
              response = await postDiagnosisRespondentDownload({
                diagnosisCd: props.cd,
                reportDateFrom: reportDateFrom,
                reportDateTo: reportDateTo,
              });
              break;
            default:
          }
          // ファイルのダウンロード
          _download(response.fileName, response.fileData);
        } catch (e) {
          if (e.response.statusCode === AppConst.httpStatusCode.entryUnprocessable) {
            // トライアルプラン（利用期間切れ）の場合はTOPに遷移
            initializeAcctState(acctState.company.cd);
            navigate('/');
          }
          showBoundary(e);
        }
      })
    );

  /* ----------------------------------------------------------------------- */
  /* プライベート関数定義 */
  /* ----------------------------------------------------------------------- */
  /**
   * ファイルダウンロードダイアログを開きます。
   */
  const _download = (fileName: string, fileData: any) => {
    // ファイルのダウンロード
    const url = window.URL.createObjectURL(new Blob([fileData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName); // ファイル名と拡張子を設定
    document.body.appendChild(link);
    link.click();

    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  /* ----------------------------------------------------------------------- */
  /* JSX */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      <ReactModal
        isOpen={props.open}
        onAfterOpen={onAfterOpen}
        onRequestClose={onClose}
        style={{
          overlay: {
            zIndex: 2000,
            backgroundColor: 'rgba(30, 30, 30,0.5)',
          },
          content: {
            maxWidth: '650px',
          },
        }}
        className="ReactModal__Content__Custom"
        ariaHideApp={false}
      >
        {initialized && (
          <div
            id="settings-modal"
            className="modal2 modal-content rounded-4 "
            style={{ maxWidth: 'none' }}
          >
            <div className="modal-header border-bottom-0">
              <button onClick={onClose} type="button" className="btn-close btn-circle-close">
                <img src="/img/icon/multiply.svg" alt="x" />
              </button>
            </div>
            <div className="modal-body">
              <h3 className="modal-title text-center mt-5" id="settings-modal-label">
                リスト出力
              </h3>
              <p className="lead text-center mb-4">指定した期間でCSV形式でダウンロードできます</p>

              <div className="row justify-content-center align-items-center mb-2">
                <p className="col-3 col-md-2 mb-0 text-end">開始日</p>
                <div className="col-6 col-md-5">
                  <div className="input-group date">
                    <FormDatePicker
                      className="form-controll-small"
                      name="reportDateFrom"
                      placeholder="無期限"
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
              </div>
              <div className="row justify-content-center align-items-center pt-1">
                <p className="col-3 col-md-2 mb-0 text-end">終了日</p>
                <div className="col-6 col-md-5">
                  <div className="input-group date">
                    <FormDatePicker
                      className="form-controll-small"
                      name="reportDateTo"
                      placeholder="無期限"
                      control={control}
                      errors={errors}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer border-top-0 justify-content-center mt-4 mb-0">
              <button
                onClick={onDownloadReport}
                type="button"
                className="btn btn-primary btn-common btn-common-w238"
                style={{ backgroundColor: '#4350b4', width: '220px' }}
              >
                レポートCSV
              </button>
            </div>

            <div className="modal-footer border-top-0 justify-content-center mt-1 mb-0">
              <button
                onClick={onDownloadRespondent}
                type="button"
                className="btn btn-primary"
                style={{ backgroundColor: '#4350b4', width: '220px' }}
              >
                獲得リストCSV
              </button>
            </div>

            <div className="modal-footer border-top-0 justify-content-center mt-3">
              <button onClick={onClose} type="button" className="btn btn-secondary">
                キャンセル
              </button>
            </div>
          </div>
        )}
      </ReactModal>
    </>
  );
}
